/**
 * 一期无评论
 */
import React, { useState, useRef, useMemo } from 'react'
import { Popover } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import styles from './ReplyBar.module.css'

import { digitalUnit } from '~/utils'
import { usePostSocial, sameAgainProps } from '~/hooks/usePostSocial'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyBtn } from '~/components/ui/MyBtn'
import { CDNImage } from '~/components/ui/CDNImage'
import { detailSwiperIndexAtom } from './atom'

import {
  PostTypes,
  VisibleType,
  AiArtworkSameType,
  AiArtworkShareSource,
} from '~/graphqls/types'
import CopyParams from '../ai/CopyParams'
import ImageToImageModal from './ImageToImageModal'
import { useValidateAuth } from '~/hooks/useValidateAuth'

type SameAgainProps = {
  post: PostTypes
  /** 隐藏次数**/
  hideCount?: boolean
  /** 隐藏图生图功能 **/
  hidePictureSame?: boolean
  handleSameAgain: (param: sameAgainProps) => void
  sameCreated?: boolean | null
  showInfo?: () => void
}

export const SameAgainBtn = ({
  handleSameAgain,
  post,
  hideCount = false,
  hidePictureSame = false,
  sameCreated = false,
  showInfo,
}: SameAgainProps) => {
  const [swiperindex] = useAtom(detailSwiperIndexAtom)
  const [visible, setVisible] = useState(false)
  // 一键图生图
  const [imgToImgVisible, setImgToImgVisible] = useState(false)
  const againContentRef = useRef<HTMLDivElement>(null)
  /** 是否有右侧菜单栏选项 */
  const hasMenu = useMemo(() => {
    return !hidePictureSame && !!post?.supportSameType?.length
  }, [hidePictureSame, post])

  const sameTypeList = useMemo(() => {
    return post?.supportSameType?.filter(
      it =>
        ![AiArtworkSameType.SVD_SAME].includes(
          it?.aiArtworkSameType as AiArtworkSameType
        )
    )
  }, [post?.supportSameType])
  return (
    <MyBtn type="primary" $dark block className="!h-[44px] ml-4 !p-0">
      {sameCreated ? (
        <div
          ref={againContentRef}
          className={classNames(
            'w-full h-full relative font-semibold text-base flex flex-col justify-center items-center',
            hasMenu && 'pr-6'
          )}
        >
          <CopyParams
            title={post.title as string}
            product={post?.aiArtworkInfo!}
            source={AiArtworkShareSource.SQUARE_POST}
            className={classNames(
              'w-full h-full relative font-semibold text-base flex flex-col justify-center items-center'
            )}
          >
            <div className="text-inherit">全参复制</div>
          </CopyParams>
        </div>
      ) : (
        <div
          ref={againContentRef}
          onClick={() => {
            handleSameAgain({
              sameType: AiArtworkSameType.CREATE_LIKEWISE,
              sameTypeName: '一键同款',
            })
          }}
          className={classNames(
            'w-full h-full relative font-semibold text-base flex flex-col justify-center items-center',
            hasMenu && 'pr-6'
          )}
        >
          <div className="flex items-center justify-center">
            <MyIcon icon="hand-pointer" className="text-base mr-2"></MyIcon>
            <span>一键同款</span>
          </div>

          {post?.visibleType === VisibleType.CREATE_LIKEWISE_COST && (
            <div className="opacity-60 text-neutral-900 text-xs leading-3">
              (支付{post.createLikewiseCast || 1}积分)
            </div>
          )}
        </div>
      )}

      {!hideCount && !!post?.sameCount ? (
        <div className="absolute -top-2 -right-2 bg-error border-2 border-neutral-200 rounded-full px-1 text-xs flex justify-center items-center h-5 min-w-[20px]">
          {post?.sameCount > 999 ? '999+' : post?.sameCount}
        </div>
      ) : null}

      {hasMenu ? (
        <Popover
          placement="topRight"
          overlayClassName={styles.overlayClass}
          open={visible}
          onOpenChange={open => {
            setVisible(open)
          }}
          getPopupContainer={() => againContentRef.current as HTMLElement}
          content={
            <div className="text-xs text-neutral-900 font-semibold min-w-[212px] ">
              {sameTypeList?.map((item, index) => (
                <div key={index}>
                  {index !== 0 && (
                    <div className="h-[1px] bg-neutral-200 mx-4" />
                  )}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-300"
                    onClick={() => {
                      setVisible(false)
                      if (
                        item.aiArtworkSameType ===
                        AiArtworkSameType.PICTURE_SAME
                      ) {
                        setImgToImgVisible(true)
                        return
                      }
                      handleSameAgain({
                        sameType: item.aiArtworkSameType,
                        pictureSameModel: item?.sameModel?.modelCode,
                        imageId: post?.imageList?.[swiperindex]?.id,
                        sameTypeName: `${item?.name || ''}${
                          item?.sameModel?.name
                            ? '（' + item?.sameModel?.name + '）'
                            : ''
                        }`,
                      })
                    }}
                  >
                    <div className="flex items-center ">
                      {item?.name || ''}
                      {item?.aiArtworkSameType ===
                        AiArtworkSameType.PICTURE_SAME && (
                        <div>
                          <MyIcon
                            icon="arrow-down-simple"
                            className="text-[20px] inline-block align-middle -rotate-90 -ml-1"
                          />
                        </div>
                      )}
                    </div>
                    {post?.visibleType === VisibleType.CREATE_LIKEWISE_COST && (
                      <div className=" flex-shrink-0 flex flex-end items-center">
                        <CDNImage
                          src="/images/icons/gold.svg"
                          className="w-4 h-4 mr-1 ml-2"
                          alt=""
                        />
                        {post?.createLikewiseCast || 1}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          }
          trigger="click"
        >
          <div className="absolute top-0 right-0 h-full w-[30%] pr-2 flex justify-end items-center">
            <span className=" w-8 h-8 flex justify-center items-center rounded-full bg-neutral-900/[0.16]">
              <MyIcon
                icon="arrow-down-simple"
                className={classNames('text-[20px]', {
                  'rotate-180': visible,
                })}
              />
            </span>
          </div>
        </Popover>
      ) : null}
      <ImageToImageModal
        post={post}
        imageId={post.imageList?.[swiperindex]?.id || 0}
        visible={imgToImgVisible}
        setVisible={setImgToImgVisible}
        showInfo={showInfo}
      />
    </MyBtn>
  )
}

export const ReplyBar = ({
  post,
  time,
  showInfo,
}: {
  post: PostTypes
  time?: number
  showInfo?: () => void
}) => {
  const { validateIsLogin } = useValidateAuth()
  const {
    liked,
    likeCount,
    handleLike,
    faved,
    favCount,
    handleFav,
    handleSameAgain,
    canSameAgain,
    showSameAgain,
  } = usePostSocial(post)

  return (
    <div
      className="absolute w-[336px] left-0 bottom-4 px-4 pr-3 py-3 flex justify-around items-center bg-neutral-200 rounded-full"
      style={{
        boxShadow: `0px 4px 16px -8px rgba(15, 15, 15, 0.2)`,
      }}
    >
      <div className="cursor-pointer" onClick={handleLike}>
        <MyIcon
          icon="heart-filled"
          className={classNames('text-lg', [
            liked ? 'text-error' : 'text-neutral-500',
          ])}
        />
        <div className="text-xs text-neutral-500 leading-tight text-center">
          {digitalUnit(likeCount)}
        </div>
      </div>
      <div className="cursor-pointer ml-4" onClick={handleFav}>
        <MyIcon
          icon="star-filled"
          className={classNames('text-lg', [
            faved ? 'text-[#FFD600]' : 'text-neutral-500',
          ])}
        />
        <div className="text-xs text-neutral-500 leading-tight text-center">
          {digitalUnit(favCount)}
        </div>
      </div>
      {showSameAgain && (
        <SameAgainBtn
          handleSameAgain={param => {
            if (!validateIsLogin()) return
            if (!canSameAgain) {
              return showInfo && showInfo()
            }
            handleSameAgain(param)
          }}
          post={post}
          sameCreated={post.sameCreated}
          showInfo={showInfo}
        />
      )}
    </div>
  )
}
